import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Store, CheckCircle, Clock, XCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  "Restaurant",
  "Cafe",
  "Bar & Pub",
  "Sports Shop",
  "Retail",
  "Fitness & Gym",
  "Services",
  "Entertainment",
  "Other"
];

export default function MerchantApplication({ user, logout }) {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form fields
  const [businessName, setBusinessName] = useState("");
  const [description, setDescription] = useState("");
  const [offerings, setOfferings] = useState("");
  const [contactEmail, setContactEmail] = useState(user?.email || "");
  const [contactPhone, setContactPhone] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [businessHours, setBusinessHours] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    fetchApplication();
  }, []);

  const fetchApplication = async () => {
    try {
      const response = await axios.get(`${API}/merchant-applications/my-application`, {
        withCredentials: true
      });
      
      if (response.data) {
        setApplication(response.data);
      }
    } catch (error) {
      console.error("Error fetching application:", error);
    } finally {
      setLoading(false);
    }
  };

  const submitApplication = async () => {
    if (!businessName || !description || !offerings || !contactEmail || !contactPhone || !category) {
      toast.error("Please fill all required fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/merchant-applications`,
        {
          business_name: businessName,
          description,
          offerings,
          contact_email: contactEmail,
          contact_phone: contactPhone,
          address: address || null,
          website: website || null,
          business_hours: businessHours || null,
          category
        },
        { withCredentials: true }
      );

      toast.success("Application submitted successfully! Admin will review shortly.");
      fetchApplication();
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/dashboard">
            <Button variant="ghost" className="text-yellow-400 hover:text-yellow-300" data-testid="back-button">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-yellow-400 uppercase" style={{ fontFamily: "'Times New Roman', Times, Georgia, serif", letterSpacing: "0.05em" }}>SCC Football</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-4xl font-bold text-white mb-8" data-testid="page-title">Become a Merchant Partner</h2>

        {application ? (
          /* Existing Application */
          <Card className="p-8 bg-slate-900/50 shadow-lg max-w-3xl mx-auto">
            <div className="text-center mb-6">
              {application.status === "pending" && (
                <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-500/20 rounded-full mb-4">
                  <Clock className="w-10 h-10 text-yellow-500" />
                </div>
              )}
              {application.status === "approved" && (
                <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-500/20 rounded-full mb-4">
                  <CheckCircle className="w-10 h-10 text-emerald-400" />
                </div>
              )}
              {application.status === "rejected" && (
                <div className="inline-flex items-center justify-center w-20 h-20 bg-red-500/20 rounded-full mb-4">
                  <XCircle className="w-10 h-10 text-red-400" />
                </div>
              )}
              
              <h3 className="text-2xl font-bold text-white mb-2">
                {application.status === "pending" && "Application Under Review"}
                {application.status === "approved" && "Application Approved!"}
                {application.status === "rejected" && "Application Rejected"}
              </h3>
              
              <p className="text-slate-400">
                {application.status === "pending" && "Admin is reviewing your merchant application. You'll be notified once processed."}
                {application.status === "approved" && "Congratulations! You can now access the Merchant Portal to manage your business and discounts."}
                {application.status === "rejected" && "Unfortunately, your application was not approved. Please contact admin for more details."}
              </p>
            </div>

            <div className="bg-[#0a0f1a] p-6 rounded-lg space-y-4">
              <h4 className="font-bold text-white mb-4">Application Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">Business Name:</p>
                  <p className="font-semibold text-white">{application.business_name}</p>
                </div>
                <div>
                  <p className="text-slate-400">Category:</p>
                  <p className="font-semibold text-white">{application.category}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-400">Description:</p>
                  <p className="font-semibold text-white">{application.description}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-400">What You Offer:</p>
                  <p className="font-semibold text-white">{application.offerings}</p>
                </div>
                <div>
                  <p className="text-slate-400">Submitted:</p>
                  <p className="font-semibold text-white">{new Date(application.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-slate-400">Status:</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    application.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                    application.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {application.status.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {application.status === "approved" && (
              <div className="mt-6">
                <Link to="/merchants">
                  <Button className="w-full bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold">
                    Go to Merchant Portal
                  </Button>
                </Link>
              </div>
            )}
          </Card>
        ) : (
          /* New Application Form */
          <Card className="p-8 bg-slate-900/50 shadow-lg max-w-3xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Store className="w-8 h-8 text-yellow-500" />
                <h3 className="text-2xl font-bold text-white">Merchant Application</h3>
              </div>
              <p className="text-slate-400">
                Join SCC Football as a merchant partner and offer exclusive deals to our members.
              </p>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium mb-2 block">Business Name *</label>
                  <Input
                    placeholder="Your Business Name"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    data-testid="business-name-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Category *</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Contact Phone *</label>
                  <Input
                    placeholder="+65 1234 5678"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    data-testid="contact-phone-input"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium mb-2 block">Contact Email *</label>
                  <Input
                    type="email"
                    placeholder="contact@yourbusiness.com"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium mb-2 block">Business Description *</label>
                  <Textarea
                    placeholder="Tell us about your business..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    data-testid="description-input"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium mb-2 block">What Can You Offer SCC Members? *</label>
                  <Textarea
                    placeholder="Describe the discounts, deals, or services you can offer to SCC Football members..."
                    value={offerings}
                    onChange={(e) => setOfferings(e.target.value)}
                    rows={4}
                    data-testid="offerings-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Example: "10-20% discount on food & beverages, Special member pricing on sports equipment"
                  </p>
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-medium mb-2 block">Address</label>
                  <Input
                    placeholder="123 Business Street, Singapore"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    data-testid="address-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Website</label>
                  <Input
                    placeholder="https://yourbusiness.com"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    data-testid="website-input"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Business Hours</label>
                  <Input
                    placeholder="Mon-Fri: 9AM-6PM"
                    value={businessHours}
                    onChange={(e) => setBusinessHours(e.target.value)}
                    data-testid="business-hours-input"
                  />
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded p-4">
                <p className="text-sm text-blue-400">
                  <strong>What happens next?</strong><br/>
                  1. Submit your application<br/>
                  2. Admin reviews your business details<br/>
                  3. Once approved, you'll get access to the Merchant Portal<br/>
                  4. Create discounts and start offering deals to SCC members
                </p>
              </div>

              <Button
                onClick={submitApplication}
                disabled={submitting}
                className="w-full bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold py-6 text-lg"
                data-testid="submit-application-button"
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
